import type { SVGProps } from 'react';

/* Base icon props */
interface IconProps extends SVGProps<SVGSVGElement> {
  size?: number;
  className?: string;
}

/* Physics Icon - Abstracted pendulum arc with motion trail
   Represents fundamental mechanics without cliché atom swirls */
export function PhysicsIcon({ size = 24, className = '', ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {/* Pendulum pivot point */}
      <circle cx="12" cy="3" r="1" fill="currentColor" stroke="none" />
      {/* Main pendulum arc */}
      <path d="M12 3 L12 14" />
      {/* Pendulum bob */}
      <circle cx="12" cy="16" r="2.5" />
      {/* Motion trail - dotted arc showing swing path */}
      <path d="M7 14 Q12 19 17 14" strokeDasharray="2 2" opacity="0.5" />
      {/* Small motion indicators */}
      <circle cx="7" cy="14" r="0.75" opacity="0.4" />
      <circle cx="17" cy="14" r="0.75" opacity="0.4" />
    </svg>
  );
}

/* Chemistry Icon - Minimalist molecular bond structure
   Represents molecular structure without bubbling beakers */
export function ChemistryIcon({ size = 24, className = '', ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {/* Molecular bonds - triangular arrangement */}
      <line x1="12" y1="5" x2="6" y2="17" />
      <line x1="12" y1="5" x2="18" y2="17" />
      <line x1="6" y1="17" x2="18" y2="17" />
      {/* Atoms - circles at vertices */}
      <circle cx="12" cy="5" r="2.5" />
      <circle cx="6" cy="17" r="2.5" />
      <circle cx="18" cy="17" r="2.5" />
    </svg>
  );
}

/* Biology Icon - Simplified cell division / mitosis
   Represents life processes without cartoon DNA helix */
export function BiologyIcon({ size = 24, className = '', ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {/* Dividing cell - two overlapping circles */}
      <circle cx="9" cy="12" r="5.5" />
      <circle cx="15" cy="12" r="5.5" />
      {/* Connection point showing division bridge */}
      <line x1="12" y1="6.5" x2="12" y2="17.5" strokeWidth="2" />
      {/* Small nuclei hints */}
      <circle cx="9" cy="12" r="1.5" fill="currentColor" stroke="none" opacity="0.3" />
      <circle cx="15" cy="12" r="1.5" fill="currentColor" stroke="none" opacity="0.3" />
    </svg>
  );
}

/* Mathematics Icon - Integral/derivative curve
   Represents calculus/analysis foundation of math */
export function MathematicsIcon({ size = 24, className = '', ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {/* Horizontal baseline (x-axis) */}
      <line x1="3" y1="18" x2="21" y2="18" />
      {/* Sigmoid curve representing function/integral */}
      <path d="M3 16 C 7 16, 9 6, 12 12 C 15 18, 17 8, 21 8" />
      {/* Vertical indicator (y-axis hint) */}
      <line x1="3" y1="18" x2="3" y2="4" opacity="0.4" />
      {/* Small area fill hint beneath curve */}
      <path d="M3 16 C 7 16, 9 6, 12 12 C 15 18, 17 8, 21 8 L 21 18 L 3 18 Z"
            fill="currentColor" fillOpacity="0.1" stroke="none" />
    </svg>
  );
}

/* Utility Icons */

/* LabIcon - Flask silhouette simplified */
export function LabIcon({ size = 24, className = '', ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {/* Flask neck */}
      <line x1="10" y1="3" x2="10" y2="9" />
      <line x1="14" y1="3" x2="14" y2="9" />
      {/* Flask body - conical bottom */}
      <path d="M8 9 L 5 19 L 19 19 L 16 9 Z" />
      {/* Flask rim */}
      <line x1="10" y1="3" x2="14" y2="3" strokeWidth="2" />
    </svg>
  );
}

/* GraphIcon - Simple line chart */
export function GraphIcon({ size = 24, className = '', ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {/* Axes */}
      <line x1="4" y1="20" x2="20" y2="20" />
      <line x1="4" y1="20" x2="4" y2="4" />
      {/* Data line with fluctuations */}
      <path d="M4 16 L 8 12 L 12 15 L 16 8 L 20 10" />
      {/* Data points */}
      <circle cx="8" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="15" r="1" fill="currentColor" stroke="none" />
      <circle cx="16" cy="8" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/* EquationIcon - f(x) simplified */
export function EquationIcon({ size = 24, className = '', ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {/* f - function notation */}
      <path d="M5 6 C 5 6, 6 4, 8 4 C 10 4, 10 6, 10 8 L 10 14 C 10 17, 9 18, 7 18 C 5 18, 4 16, 4 16" />
      {/* ( - opening parenthesis */}
      <path d="M12 8 Q 12 12, 14 12 Q 16 12, 16 8" opacity="0.7" />
      {/* x - variable hint */}
      <path d="M18 10 L 20 8 M 18 10 L 20 12" opacity="0.7" />
      {/* Equals bar hint */}
      <line x1="12" y1="15" x2="20" y2="15" opacity="0.5" />
      <line x1="12" y1="18" x2="20" y2="18" opacity="0.5" />
    </svg>
  );
}

/* DataIcon - Grid of dots */
export function DataIcon({ size = 24, className = '', ...props }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {/* 3x3 grid of data points */}
      <circle cx="7" cy="7" r="1.5" />
      <circle cx="12" cy="7" r="1.5" />
      <circle cx="17" cy="7" r="1.5" />
      <circle cx="7" cy="12" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="17" cy="12" r="1.5" />
      <circle cx="7" cy="17" r="1.5" />
      <circle cx="12" cy="17" r="1.5" />
      <circle cx="17" cy="17" r="1.5" />
    </svg>
  );
}
